"use client";

import { useEffect, useMemo, useState, type CSSProperties } from "react";
import {
    ExpertReviewCard,
    ExpertReviewsSecondary,
    ExpertTabs,
    Icon,
    ImageWrapper,
    KeyHighlight,
    ReviewSummary,
    cn,
    mapExpertReviewData,
    type ExpertReviewDataApi,
} from "@farm-junction/design-library";

/** Design-library ExpertReviewHighlights uses `key={text}`; API can repeat the same line. */
function ExpertReviewHighlightsList({
    pros = [],
    cons = [],
    className,
}: {
    pros?: string[];
    cons?: string[];
    className?: string;
}) {
    const items = [
        ...pros.map((text) => ({ type: "positive" as const, text })),
        ...cons.map((text) => ({ type: "negative" as const, text })),
    ];

    if (!items.length) {
        return null;
    }

    return (
        <div
            className={cn(
                "flex flex-col gap-2 md:gap-[9px] md:border-t md:border-border md:pt-[14px]",
                className,
            )}
        >
            {items.map((item, index) => (
                <div
                    key={`${item.type}-${index}`}
                    className="flex items-center gap-[9px]"
                >
                    <span
                        className={cn(
                            "flex size-5 shrink-0 items-center justify-center rounded-md",
                            item.type === "positive"
                                ? "bg-[#F0FDF4] text-[#008C8B]"
                                : "bg-[#FEF3C7] text-[#363940]",
                        )}
                    >
                        {item.type === "positive" ? (
                            <Icon name="check" className="size-[11px]" />
                        ) : (
                            <span
                                className="block h-0.5 w-2.5 rounded-full bg-current"
                                aria-hidden
                            />
                        )}
                    </span>
                    <KeyHighlight
                        value={item.text}
                        className="max-w-none min-w-0 flex-1"
                        slotProps={{
                            valueContainerProps: { className: "w-full" },
                            valueProps: {
                                className:
                                    "text-[13px] leading-[18px] text-on-secondary font-normal",
                                isTruncated: false,
                            },
                        }}
                    />
                </div>
            ))}
        </div>
    );
}

const EXPERT_REVIEW_MODAL_Z =
    "z-9999 items-end justify-center bg-black/40 md:items-stretch md:justify-end [&_button]:cursor-pointer";

/** Car Junction palette — applied via slot classNames + inherited CSS vars (no data-brand). */
const CJ_PRIMARY = "#008C8B";
const CJ_PRIMARY_HOVER = "#00ADAC";
const CJ_BORDER = "#EBE6E7";
const CJ_ON_SECONDARY = "#363940";

const CJ_THEME_VARS: CSSProperties = {
    ["--color-primary" as string]: CJ_PRIMARY,
    ["--color-primary-hover" as string]: CJ_PRIMARY_HOVER,
    ["--color-on-primary" as string]: "#FFFFFF",
    ["--color-brand-50" as string]: "#E5F4F4",
    ["--color-brand-100" as string]: "#B8E0DF",
    ["--color-brand-600" as string]: "#006E6D",
    ["--color-brand-800" as string]: "#737373",
    ["--color-secondary" as string]: "#F0FDF4",
    ["--color-on-secondary" as string]: CJ_ON_SECONDARY,
    ["--color-border" as string]: CJ_BORDER,
    ["--color-surface" as string]: "#FFFFFF",
    ["--color-on-surface" as string]: "#101828",
};

const CJ_TAB_ACTIVE = cn(
    "h-[38px] shrink-0 gap-[9px] rounded-xl px-[15px] py-[9px] text-sm cursor-pointer",
    "!border-[#006E6D] !bg-[#008C8B] !font-medium !text-white hover:!bg-[#00ADAC]",
);
const CJ_TAB_INACTIVE = cn(
    "h-[38px] shrink-0 gap-[9px] rounded-xl px-[15px] py-[9px] text-sm cursor-pointer",
    "!border-[#EBE6E7] !bg-white !font-normal !text-[#363940] hover:!bg-[#F9F9F9]",
);
const CJ_TAB_PILL_ACTIVE =
    "!rounded-full !bg-[#008C8B] !px-3 !py-[9px] !text-[13px] !font-medium !text-white hover:!bg-[#00ADAC]";
const CJ_TAB_PILL_INACTIVE =
    "!rounded-full !bg-transparent !px-3 !py-[9px] !text-[13px] !font-medium !text-[#363940] hover:!bg-white";

const CJ_PRIMARY_BUTTON = cn(
    "h-auto w-full gap-[7px] rounded-xl px-4 py-[13px] text-sm font-medium md:w-auto md:px-[18px] md:py-[11px] md:text-[13px] cursor-pointer",
    "!border-transparent !bg-[#008C8B] !text-white hover:!bg-[#00ADAC] focus-visible:!outline-[#008C8B]",
);

const CJ_TAB_SLOT_PROPS = {
    activeTabProps: { className: CJ_TAB_ACTIVE },
    inactiveTabProps: { className: CJ_TAB_INACTIVE },
};

const CJ_MODAL_TAB_SLOT_PROPS = {
    activeTabProps: { className: CJ_TAB_PILL_ACTIVE },
    inactiveTabProps: { className: CJ_TAB_PILL_INACTIVE },
};

/** CMS HTML sometimes wraps body copy in h1–h6; keep text for view source without extra headings. */
function flattenHeadingTagsInHtml(html: string): string {
    if (!html.trim()) {
        return "";
    }

    return html.replace(/<\/?h[1-6](\s[^>]*)?>/gi, (tag) =>
        tag.startsWith("</") ? "</p>" : "<p>",
    );
}

/** Unrated sections arrive as `rating: null`; the library needs a number, so they become 0. */
function toRating(value: unknown): number {
    const rating = Number(value);
    return Number.isFinite(rating) ? rating : 0;
}

/** The API sends `rating: null` for unrated sections; the library types require a number. */
type ExpertReviewItemInput = Omit<ExpertReviewDataApi["expert_review"][number], "rating"> & {
    rating?: number | null;
};
export type ExpertReviewDataInput = Omit<ExpertReviewDataApi, "expert_review"> & {
    expert_review: ExpertReviewItemInput[];
};

/** mapExpertReviewData calls `.map` / `[0]` on these fields without null checks. */
function normalizeExpertReviewData(
    data: ExpertReviewDataInput,
): ExpertReviewDataApi {
    const asStrings = (value: unknown): string[] =>
        Array.isArray(value) ? value.filter(Boolean).map(String) : [];

    return {
        ...data,
        expert_review_summary: String(data?.expert_review_summary ?? ""),
        expert_score: Number(data?.expert_score) || 0,
        expert_review: (Array.isArray(data?.expert_review)
            ? data.expert_review
            : []
        )
            .filter(Boolean)
            .map((item) => ({
                ...item,
                heading: String(item.heading ?? ""),
                title: String(item.title ?? ""),
                summary: String(item.summary ?? ""),
                content: String(item.content ?? ""),
                rating: toRating(item.rating),
                pros: asStrings(item.pros),
                cons: asStrings(item.cons),
                images: asStrings(item.images),
            })),
    };
}

/** Tabs already use API `heading` from mapExpertReviewData; card/modal need API `title`. */
function withExpertReviewCardTitles(
    mapped: ReturnType<typeof mapExpertReviewData>,
    reviews: ExpertReviewDataApi["expert_review"],
): ReturnType<typeof mapExpertReviewData> {
    const published = reviews.filter(
        (item) => !item.status || item.status === "published",
    );

    return {
        ...mapped,
        contentData: mapped.contentData.map((item, index) => {
            const raw = published[index];
            const title =
                raw?.title?.trim() ||
                raw?.heading?.trim() ||
                item.title;
            return { ...item, title, imageAlt: title ?? item.imageAlt };
        }),
    };
}

export interface ExpertReviewsSectionLibProps {
    expertReviewData: ExpertReviewDataInput;
    productTitle: string;
    maxScore?: number;
    expertScoreLabel?: string;
    scoreOutOfLabel?: string;
    readMoreLabel?: string;
    fullReviewLabel?: string;
    upNextLabel?: string;
    assetsBaseUrl?: string;
}

export default function ExpertReviewsSectionLib({
    expertReviewData: rawExpertReviewData,
    productTitle,
    maxScore = 5,
    expertScoreLabel = "Expert score",
    scoreOutOfLabel = "out of 5",
    readMoreLabel = "Read full review",
    fullReviewLabel = "Full review",
    upNextLabel = "Up next",
    assetsBaseUrl = process.env.NEXT_PUBLIC_CDN_URL ?? "",
}: ExpertReviewsSectionLibProps) {
    const expertReviewData = useMemo(
        () => normalizeExpertReviewData(rawExpertReviewData),
        [rawExpertReviewData],
    );

    const mapped = useMemo(
        () =>
            withExpertReviewCardTitles(
                mapExpertReviewData(expertReviewData, {
                    productTitle,
                    maxScore,
                    assetsBaseUrl,
                }),
                expertReviewData.expert_review,
            ),
        [expertReviewData, productTitle, maxScore, assetsBaseUrl],
    );

    const [open, setOpen] = useState(false);
    const [activeTab, setActiveTab] = useState<string | null>(null);

    const effectiveActiveTab =
        activeTab && mapped.tabsData.some((tab) => tab.id === activeTab)
            ? activeTab
            : (mapped.tabsData[0]?.id ?? "");

    useEffect(() => {
        if (!open) return;

        const html = document.documentElement;
        const previousBodyOverflow = document.body.style.overflow;
        const previousHtmlOverflow = html.style.overflow;

        document.body.style.overflow = "hidden";
        html.style.overflow = "hidden";

        return () => {
            document.body.style.overflow = previousBodyOverflow;
            html.style.overflow = previousHtmlOverflow;
        };
    }, [open]);

    const activeContent = mapped.contentData.find(
        (item) => item.id === effectiveActiveTab,
    );
    const activeIndex = mapped.tabsData.findIndex(
        (tab) => tab.id === effectiveActiveTab,
    );
    const nextTab =
        activeIndex >= 0 ? mapped.tabsData[activeIndex + 1] : undefined;
    const nextContent = nextTab
        ? mapped.contentData.find((item) => item.id === nextTab.id)
        : undefined;

    // No fallback to the overall score: that would show the model's score on a section.
    const sectionScore = activeContent?.score ?? 0;

    if (!mapped.tabsData.length) {
        return null;
    }

    return (
        <section
            className="flex flex-col gap-4"
            style={CJ_THEME_VARS}
        >
            <ReviewSummary
                title={mapped.title}
                description={mapped.description}
                score={mapped.score}
                maxScore={mapped.maxScore}
                expertScoreLabel={expertScoreLabel}
                scoreOutOfLabel={scoreOutOfLabel}
            />

            <ExpertTabs
                tabs={mapped.tabsData}
                activeTab={effectiveActiveTab}
                onSelect={setActiveTab}
                ariaLabel="Expert review categories"
                variant="chip"
                slotProps={CJ_TAB_SLOT_PROPS}
                
                
            />

            <div className="flex flex-col gap-4 md:flex-row md:items-start md:gap-5">
                {activeContent?.imageSrc ? (
                    <div className="relative aspect-[16/10] w-full min-h-[200px] overflow-hidden rounded-2xl md:aspect-auto md:min-h-[280px] md:flex-1 md:rounded-[20px]">
                        <ImageWrapper
                            src={activeContent.imageSrc}
                            alt={activeContent.imageAlt ?? activeContent.title ?? "Expert review"}
                            className="absolute inset-0 h-full w-full object-cover object-center"
                        />
                    </div>
                ) : null}

                <ExpertReviewCard
                    className="w-full border-[#EBE6E7] md:w-[405px] md:max-w-[405px] md:shrink-0 md:self-stretch [&>div:first-child>h3:empty]:hidden"
                    title={activeContent?.title}
                    score={sectionScore}
                    maxScore={mapped.maxScore}
                    excerpt={
                        activeContent?.excerpt ? (
                            <span className="line-clamp-4">{activeContent.excerpt}</span>
                        ) : undefined
                    }
                    reviewerName={activeContent?.reviewerName}
                    readMoreLabel={readMoreLabel}
                    onReadMore={() => setOpen(true)}
                    buttonProps={{ className: CJ_PRIMARY_BUTTON }}
                    highlights={
                        activeContent ? (
                            <ExpertReviewHighlightsList
                                pros={activeContent.pros ?? []}
                                cons={activeContent.cons ?? []}
                            />
                        ) : null
                    }  
                />
            </div>

            <div className="hidden" aria-hidden="true">
                {mapped.contentData
                    .map((item) => {
                        const raw = expertReviewData.expert_review.find(
                            (review) => review._id === item.id,
                        );
                        const html = String(raw?.content ?? "").trim();
                        if (!html) {
                            return null;
                        }

                        const sectionTitle =
                            item.title?.trim() ||
                            raw?.heading?.trim() ||
                            raw?.title?.trim() ||
                            mapped.tabsData.find((tab) => tab.id === item.id)
                                ?.title;

                        return (
                            <article
                                key={`expert-review-source-${item.id}`}
                                id={`expert-review-panel-${item.id}`}
                            >
                                {sectionTitle ? <h3>{sectionTitle}</h3> : null}
                                {item.excerpt ? <p>{item.excerpt}</p> : null}
                                <div
                                    className="expert-review-html [&_p]:mb-3.5 [&_p:last-child]:mb-0"
                                    dangerouslySetInnerHTML={{
                                        __html: flattenHeadingTagsInHtml(html),
                                    }}
                                />
                            </article>
                        );
                    })}
            </div>

            <ExpertReviewsSecondary
                isOpen={open}
                onClose={() => setOpen(false)}
                title={productTitle}
                fullReviewLabel={fullReviewLabel}
                upNextLabel={upNextLabel}
                tabs={mapped.tabsData}
                activeTab={effectiveActiveTab}
                onSelect={setActiveTab}
                activeContent={activeContent}
                nextTab={nextTab}
                nextContent={nextContent}
                contentScore={sectionScore}
                maxScore={mapped.maxScore}
                slotProps={{
                    ...CJ_MODAL_TAB_SLOT_PROPS,
                    modalPortalProps: {
                        className: EXPERT_REVIEW_MODAL_Z,
                        style: CJ_THEME_VARS,
                    },
                    buttonProps: {
                        className: cn(
                            "!mt-3.5 !h-auto !w-full !items-center !justify-between !gap-3 !rounded-2xl !border !border-[#B8E0DF] !bg-[#F9F9F9] !px-3.5 !py-3.5 !text-left hover:!bg-[#F9F9F9] cursor-pointer md:!mt-4 md:!h-[88px] md:!rounded-2xl md:!px-4 md:!py-3.5",
                        ),
                    },
                }}
            />
        </section>
    );
}
